import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function TicketFinder() {
  const { data: vans } = useFetch("/vans");
  const { data: drivers } = useFetch("/drivers");
  const navigate = useNavigate();
  const [reg, setReg] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ticketForm, setTicketForm] = useState(null);

  const search = async () => {
    if (!reg || !date) { toast.error("Enter van registration and date"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/finder", { van_reg: reg, date, time });
      setResult(data);
      if (data.matches.length === 0) toast.info("No driver found — you can manually assign");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startTicket = (match) => {
    setTicketForm({
      driver_id: match?.driver_id || "", van_reg: result.van_reg, date: result.date, time,
      location: "", reference: "", type: "Parking (PCN)", amount: "", notes: "", deduct_from_earnings: false,
    });
  };
  const setT = (k) => (e) => setTicketForm({ ...ticketForm, [k]: e.target?.value ?? e });

  const createTicket = async () => {
    try {
      await api.post("/tickets", { ...ticketForm, amount: parseFloat(ticketForm.amount || 0) });
      toast.success("Ticket created");
      setTicketForm(null);
      navigate("/tickets");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fp-fade-in">
      <PageHeader title="Ticket Finder" subtitle="Find who was driving a van on a given date, then raise the ticket" testId="ticket-finder-page" />

      <Card className="p-6 mb-6">
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Van Registration</Label>
            <Input value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="EF70 KLP" className="mt-1.5 font-mono" data-testid="finder-reg-input" />
          </div>
          <div><Label>Ticket Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="finder-date-input" /></div>
          <div><Label>Time (optional)</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5" data-testid="finder-time-input" /></div>
          <Button onClick={search} disabled={busy} data-testid="finder-search-button">
            {busy ? <Icons.LoaderCircle className="h-4 w-4 animate-spin" /> : <><Icons.Search className="h-4 w-4 mr-1.5" /> Search</>}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-6" data-testid="finder-results">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <Icons.Users className="h-4 w-4" /> {result.matches.length} possible driver{result.matches.length !== 1 ? "s" : ""} for <Plate reg={result.van_reg} /> on {ukDate(result.date)}
          </h3>
          {result.matches.length === 0 ? (
            <div className="text-center py-6">
              <EmptyState icon="UserX" title="No driver assignment found" subtitle="No historical record matches this van and date." />
              <Button onClick={() => startTicket(null)} data-testid="manual-assign-button"><Icons.UserPlus className="h-4 w-4 mr-1.5" /> Manually assign ticket</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {result.matches.map((m) => (
                <div key={m.driver_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-border rounded-lg p-4" data-testid={`match-${m.driver_code}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/15 text-primary grid place-items-center text-sm font-bold uppercase">{m.driver_name.slice(0, 2)}</div>
                    <div>
                      <p className="font-medium">{m.driver_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{m.driver_code} · {m.phone}</p>
                      <p className="text-xs text-muted-foreground">Assigned {ukDate(m.assignment_start)} → {m.assignment_end ? ukDate(m.assignment_end) : "Present"}</p>
                    </div>
                  </div>
                  <Button onClick={() => startTicket(m)} data-testid={`create-ticket-${m.driver_code}`}>
                    <Icons.Receipt className="h-4 w-4 mr-1.5" /> Create Ticket
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Dialog open={!!ticketForm} onOpenChange={() => setTicketForm(null)}>
        <DialogContent className="max-w-lg" data-testid="finder-ticket-dialog">
          <DialogHeader><DialogTitle>Create Ticket</DialogTitle></DialogHeader>
          {ticketForm && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Driver</Label>
                <Select value={ticketForm.driver_id || "none"} onValueChange={(v) => setT("driver_id")(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Van</Label><Input value={ticketForm.van_reg} onChange={setT("van_reg")} className="mt-1.5 font-mono" /></div>
              <div><Label>Date</Label><Input type="date" value={ticketForm.date} onChange={setT("date")} className="mt-1.5" /></div>
              <div><Label>Time</Label><Input type="time" value={ticketForm.time} onChange={setT("time")} className="mt-1.5" /></div>
              <div><Label>Type</Label><Input value={ticketForm.type} onChange={setT("type")} className="mt-1.5" /></div>
              <div><Label>Amount (£)</Label><Input type="number" value={ticketForm.amount} onChange={setT("amount")} className="mt-1.5" /></div>
              <div><Label>Reference</Label><Input value={ticketForm.reference} onChange={setT("reference")} className="mt-1.5" /></div>
              <div><Label>Location</Label><Input value={ticketForm.location} onChange={setT("location")} className="mt-1.5" /></div>
              <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                <Checkbox id="deduct" checked={ticketForm.deduct_from_earnings} onCheckedChange={(v) => setT("deduct_from_earnings")(!!v)} data-testid="finder-deduct-checkbox" />
                <Label htmlFor="deduct" className="cursor-pointer">Deduct from driver earnings</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketForm(null)}>Cancel</Button>
            <Button onClick={createTicket} data-testid="finder-save-ticket-button">Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
